/**
 * React hook for managing org invites via the Pages proxies.
 *
 * Wraps `/api/organizations/invites` (list + create) and
 * `/api/organizations/invites/{id}/resend` and `/revoke`. Thrown errors
 * carry an upstream-derived `name` (e.g. `'SeatLimitExceededError'`,
 * `'ResendCooldownError'`) so callers can branch on `error.name` rather
 * than string-matching messages.
 *
 * @module lib/useInvites
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSupabase } from './SupabaseProvider';
import { captureException } from './telemetry/posthog/frontend';

export type InviteListErrorKind = 'reauth' | 'forbidden' | 'retryable';

/** Safe list-fetch copy exposed to authenticated settings surfaces. */
const INVITES_LIST_ERROR_COPY: Record<InviteListErrorKind, string> = {
  reauth: "We need to check your sign-in again.",
  forbidden: "You don't have access to pending invites.",
  retryable: "Couldn't load pending invites.",
};

class InviteListRequestError extends Error {
  constructor(readonly status: number) {
    super('Pending invites request failed');
    this.name = 'InviteListRequestError';
  }
}

class InviteSessionRequiredError extends Error {
  constructor() {
    super('Invite session required');
    this.name = 'InviteSessionRequiredError';
  }
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Single invite row as returned by the metering-svc list endpoint. */
export interface Invite {
  id: string;
  email: string;
  role_name: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expires_at: string;
  last_sent_at: string | null;
  send_count: number;
  created_at: string;
}

/** Result payload for `create`. */
export interface CreateInviteResult {
  invite_id: string;
  email_masked: string;
  expires_at: string;
  /** Whether the Brevo transactional email went out. `false` means the invite
   *  row exists but the email failed - admin can retry via the Resend button. */
  sent: boolean;
}

/** Result payload for `resend`. */
export interface ResendInviteResult {
  send_count: number;
  last_sent_at: string;
}

/** Return type for the `useInvites` hook. */
export interface UseInvites {
  /** Current list of invites for the org (initially empty while loading). */
  invites: Invite[];
  /** Whether the list is currently being fetched. */
  loading: boolean;
  /** Human-readable error message from the last list fetch, or null. */
  error: string | null;
  /** Stable recovery class for the last list fetch, or null. */
  errorKind: InviteListErrorKind | null;
  /** Re-fetch the list. */
  refresh: () => Promise<void>;
  /** Create an invite. Throws named errors on upstream failures. */
  create: (args: { email: string; role: string }) => Promise<CreateInviteResult>;
  /** Resend a pending invite. Throws named errors on cooldown / cap. */
  resend: (inviteId: string) => Promise<ResendInviteResult>;
  /** Revoke a pending invite. */
  revoke: (inviteId: string) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Error classes - one per stable upstream error code
// ---------------------------------------------------------------------------

/**
 * Base class for invite errors. Documented action/status/code outcomes receive
 * a typed `name`; unexpected combinations retain the generic name for safe UI
 * fallback copy. `detail` preserves the upstream object when provided.
 */
export class InviteApiError extends Error {
  /** Stable upstream code, retained separately from untrusted response copy. */
  code: string;
  /** Upstream `detail` object (may contain `retry_after_seconds`, etc). */
  detail?: unknown;
  /** HTTP status code. */
  status: number;
  constructor(
    name: string,
    message: string,
    status: number,
    detail?: unknown,
    code = 'invite_api_error',
  ) {
    super(message);
    this.name = name;
    this.code = code;
    this.status = status;
    this.detail = detail;
  }
}

function isBoundedString(value: unknown, maxLength: number): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= maxLength;
}

function isIsoDate(value: unknown): value is string {
  return isBoundedString(value, 64) && Number.isFinite(Date.parse(value));
}

function parseInvite(value: unknown): Invite | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  if (
    !isBoundedString(row.id, 200)
    || !isBoundedString(row.email, 320)
    || !isBoundedString(row.role_name, 100)
    || (row.status !== 'pending'
      && row.status !== 'accepted'
      && row.status !== 'expired'
      && row.status !== 'revoked')
    || !isIsoDate(row.expires_at)
    || (row.last_sent_at !== null && !isIsoDate(row.last_sent_at))
    || !Number.isInteger(row.send_count)
    || (row.send_count as number) < 0
    || !isIsoDate(row.created_at)
  ) return null;

  return {
    id: row.id,
    email: row.email,
    role_name: row.role_name,
    status: row.status,
    expires_at: row.expires_at,
    last_sent_at: row.last_sent_at,
    send_count: row.send_count as number,
    created_at: row.created_at,
  };
}

function parseInviteList(value: unknown): Invite[] | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const invites = (value as Record<string, unknown>).invites;
  if (!Array.isArray(invites)) return null;
  const parsed = invites.map(parseInvite);
  return parsed.every((invite): invite is Invite => invite !== null) ? parsed : null;
}

function parseCreateInviteResult(value: unknown): CreateInviteResult | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const result = value as Record<string, unknown>;
  if (
    !isBoundedString(result.invite_id, 200)
    || !isBoundedString(result.email_masked, 320)
    || !isIsoDate(result.expires_at)
    || typeof result.sent !== 'boolean'
  ) return null;
  return {
    invite_id: result.invite_id,
    email_masked: result.email_masked,
    expires_at: result.expires_at,
    sent: result.sent,
  };
}

function parseResendInviteResult(value: unknown): ResendInviteResult | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const result = value as Record<string, unknown>;
  if (
    !Number.isInteger(result.send_count)
    || (result.send_count as number) < 0
    || !isIsoDate(result.last_sent_at)
  ) return null;
  return {
    send_count: result.send_count as number,
    last_sent_at: result.last_sent_at,
  };
}

function invalidMutationResponse(action: 'create' | 'resend'): InviteApiError {
  return new InviteApiError(
    'InviteApiError',
    `Invalid invite ${action} response`,
    200,
    undefined,
    'invalid_response',
  );
}

/** Maps upstream `error` codes to the JS error class name. */
const ERROR_CODE_TO_NAME: Record<string, string> = {
  seat_limit_exceeded: 'SeatLimitExceededError',
  duplicate_pending_invite: 'DuplicatePendingInviteError',
  email_already_member: 'EmailAlreadyMemberError',
  invite_rate_limit_hour: 'InviteRateLimitHourError',
  invite_rate_limit_day: 'InviteRateLimitDayError',
  org_pending_invites_cap: 'OrgPendingInvitesCapError',
  resend_cooldown: 'ResendCooldownError',
  resend_cap_reached: 'ResendCapReachedError',
  invite_not_pending: 'InviteNotPendingError',
  forbidden: 'ForbiddenError',
};

type InviteMutationAction = 'create' | 'resend' | 'revoke';

/** Documented domain outcomes, keyed by the action and exact HTTP status/code pair. */
const EXPECTED_MUTATION_OUTCOMES: Record<InviteMutationAction, ReadonlySet<string>> = {
  create: new Set([
    '403:forbidden',
    '403:seat_limit_exceeded',
    '409:duplicate_pending_invite',
    '409:email_already_member',
    '409:org_pending_invites_cap',
    '429:invite_rate_limit_hour',
    '429:invite_rate_limit_day',
  ]),
  resend: new Set([
    '403:forbidden',
    '410:invite_not_pending',
    '429:resend_cooldown',
    '429:resend_cap_reached',
  ]),
  revoke: new Set([
    '403:forbidden',
    '410:invite_not_pending',
  ]),
};

function isExpectedMutationOutcome(
  action: InviteMutationAction,
  status: number,
  code: string,
): boolean {
  return EXPECTED_MUTATION_OUTCOMES[action].has(`${status}:${code}`);
}

/** Build a named `InviteApiError` from a non-2xx upstream body. */
function errorFromBody(
  body: { error?: string; detail?: unknown; message?: string } | null,
  status: number,
  action: InviteMutationAction,
): InviteApiError {
  const code = body?.error ?? 'invite_api_error';
  const name = isExpectedMutationOutcome(action, status, code)
    ? ERROR_CODE_TO_NAME[code] ?? 'InviteApiError'
    : 'InviteApiError';
  const message = typeof body?.message === 'string'
    ? body.message
    : typeof body?.detail === 'string'
      ? body.detail
      : code;
  return new InviteApiError(name, message, status, body?.detail, code);
}

/** Expected domain outcomes are presented by callers, not reported as defects. */
function captureUnexpectedMutationError(
  error: unknown,
  action: InviteMutationAction,
  context: string,
  properties: Record<string, unknown>,
): void {
  if (
    error instanceof InviteApiError
    && isExpectedMutationOutcome(action, error.status, error.code)
  ) return;
  const reportableError = error instanceof InviteApiError
    ? new Error(`Invite API request failed (${error.status})`)
    : new Error(`Invite ${action} request failed`);
  captureException(
    reportableError,
    context,
    properties,
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Invite management hook scoped to a single org. Pass `null` to disable
 * fetching (useful before the active org is loaded).
 */
export function useInvites(orgId: string | null): UseInvites {
  const { supabase } = useSupabase();

  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState<boolean>(orgId !== null);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<InviteListErrorKind | null>(null);

  /** Current render ownership, updated before effects or old promises resume. */
  const activeOrgIdRef = useRef(orgId);
  activeOrgIdRef.current = orgId;

  /** Stable ref to the supabase client - avoids retriggering the list effect. */
  const supabaseRef = useRef(supabase);
  supabaseRef.current = supabase;

  /** Organization that owns the currently cached invite rows. */
  const invitesOrgIdRef = useRef<string | null>(null);

  /** Organization that owns the current list error and loading state. */
  const errorOrgIdRef = useRef<string | null>(null);
  const loadingOrgIdRef = useRef<string | null>(orgId);

  /** Monotonic authority for list loads so older org responses cannot win. */
  const loadGenerationRef = useRef(0);

  /** Fetch the Authorization header with a fresh access token. */
  const getAuthHeader = useCallback(async (): Promise<string> => {
    const { data: { session } } = await supabaseRef.current.auth.getSession();
    if (!session?.access_token) {
      throw new InviteSessionRequiredError();
    }
    return `Bearer ${session.access_token}`;
  }, []);

  /**
   * Fetch the list of invites for the current org. Always resolves; errors
   * are recorded in hook state and captured to PostHog.
   */
  const loadInvites = useCallback(async (): Promise<void> => {
    const loadOrgId = orgId;
    if (activeOrgIdRef.current !== loadOrgId) return;
    const loadGeneration = ++loadGenerationRef.current;
    if (!loadOrgId) {
      setInvites([]);
      invitesOrgIdRef.current = null;
      setError(null);
      setErrorKind(null);
      errorOrgIdRef.current = null;
      setLoading(false);
      loadingOrgIdRef.current = null;
      return;
    }
    if (invitesOrgIdRef.current !== loadOrgId) {
      setInvites([]);
    }
    setLoading(true);
    loadingOrgIdRef.current = loadOrgId;
    setError(null);
    setErrorKind(null);
    errorOrgIdRef.current = loadOrgId;
    try {
      const authHeader = await getAuthHeader();
      if (activeOrgIdRef.current !== loadOrgId) return;
      const url = `/api/organizations/invites?org_id=${encodeURIComponent(loadOrgId)}&status=pending`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { Authorization: authHeader },
      });
      if (!response.ok) {
        throw new InviteListRequestError(response.status);
      }
      const sanitized = parseInviteList(await response.json());
      if (!sanitized) throw new Error('Invalid pending invites response');
      // Defence-in-depth: strip any `token` field the upstream might return.
      // The metering-svc response schema should already narrow to the
      // non-sensitive columns, but stripping here means a mis-release on
      // the server can't leak bearer credentials into client state.
      if (
        loadGeneration !== loadGenerationRef.current
        || activeOrgIdRef.current !== loadOrgId
      ) return;
      setInvites(sanitized);
      invitesOrgIdRef.current = loadOrgId;
    } catch (err) {
      if (
        loadGeneration !== loadGenerationRef.current
        || activeOrgIdRef.current !== loadOrgId
      ) return;
      captureException(
        new Error('Pending invites request failed'),
        'useInvites.loadInvites',
        { orgId: loadOrgId },
      );
      const nextErrorKind: InviteListErrorKind = err instanceof InviteSessionRequiredError
        || (err instanceof InviteListRequestError && err.status === 401)
        ? 'reauth'
        : err instanceof InviteListRequestError && err.status === 403
          ? 'forbidden'
          : 'retryable';
      setError(INVITES_LIST_ERROR_COPY[nextErrorKind]);
      setErrorKind(nextErrorKind);
      errorOrgIdRef.current = loadOrgId;
    } finally {
      if (
        loadGeneration === loadGenerationRef.current
        && activeOrgIdRef.current === loadOrgId
      ) {
        setLoading(false);
        loadingOrgIdRef.current = loadOrgId;
      }
    }
  }, [orgId, getAuthHeader]);

  // Initial load when orgId changes. `loadInvites` is stable except when
  // `orgId` itself changes, so this effect runs exactly once per orgId.
  useEffect(() => {
    void loadInvites();
  }, [loadInvites]);

  /** Re-fetch the invite list. */
  const refresh = useCallback(async (): Promise<void> => {
    await loadInvites();
  }, [loadInvites]);

  /** Create an invite via the Pages proxy. Refreshes the list on success. */
  const create = useCallback(
    async ({ email, role }: { email: string; role: string }): Promise<CreateInviteResult> => {
      if (!orgId) {
        const err = new Error('No active organization');
        captureException(err, 'useInvites.create', { orgId });
        throw err;
      }
      try {
        const authHeader = await getAuthHeader();
        const response = await fetch('/api/organizations/invites', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authHeader,
          },
          body: JSON.stringify({ org_id: orgId, email, role }),
        });
        if (!response.ok) {
          const body = (await response.json().catch(() => null)) as { error?: string; detail?: unknown; message?: string } | null;
          throw errorFromBody(body, response.status, 'create');
        }
        const result = parseCreateInviteResult(await response.json());
        if (!result) throw invalidMutationResponse('create');
        if (activeOrgIdRef.current === orgId) await loadInvites();
        return result;
      } catch (err) {
        if (activeOrgIdRef.current === orgId) {
          captureUnexpectedMutationError(err, 'create', 'useInvites.create', { orgId });
        }
        throw err;
      }
    },
    [orgId, getAuthHeader, loadInvites],
  );

  /** Resend a pending invite. Refreshes the list on success. */
  const resend = useCallback(
    async (inviteId: string): Promise<ResendInviteResult> => {
      if (!orgId) {
        const err = new Error('No active organization');
        captureException(err, 'useInvites.resend', { orgId, inviteId });
        throw err;
      }
      try {
        const authHeader = await getAuthHeader();
        const response = await fetch(
          `/api/organizations/invites/${encodeURIComponent(inviteId)}/resend`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: authHeader,
            },
            body: JSON.stringify({ org_id: orgId }),
          },
        );
        if (!response.ok) {
          const body = (await response.json().catch(() => null)) as { error?: string; detail?: unknown; message?: string } | null;
          throw errorFromBody(body, response.status, 'resend');
        }
        const result = parseResendInviteResult(await response.json());
        if (!result) throw invalidMutationResponse('resend');
        if (activeOrgIdRef.current === orgId) await loadInvites();
        return result;
      } catch (err) {
        if (activeOrgIdRef.current === orgId) {
          captureUnexpectedMutationError(err, 'resend', 'useInvites.resend', { orgId, inviteId });
        }
        throw err;
      }
    },
    [orgId, getAuthHeader, loadInvites],
  );

  /** Revoke a pending invite. Refreshes the list on success. */
  const revoke = useCallback(
    async (inviteId: string): Promise<void> => {
      if (!orgId) {
        const err = new Error('No active organization');
        captureException(err, 'useInvites.revoke', { orgId, inviteId });
        throw err;
      }
      try {
        const authHeader = await getAuthHeader();
        const response = await fetch(
          `/api/organizations/invites/${encodeURIComponent(inviteId)}/revoke`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: authHeader,
            },
            body: JSON.stringify({ org_id: orgId }),
          },
        );
        if (!response.ok) {
          const body = (await response.json().catch(() => null)) as { error?: string; detail?: unknown; message?: string } | null;
          throw errorFromBody(body, response.status, 'revoke');
        }
        if (activeOrgIdRef.current === orgId) await loadInvites();
      } catch (err) {
        if (activeOrgIdRef.current === orgId) {
          captureUnexpectedMutationError(err, 'revoke', 'useInvites.revoke', { orgId, inviteId });
        }
        throw err;
      }
    },
    [orgId, getAuthHeader, loadInvites],
  );

  return {
    invites: invitesOrgIdRef.current === orgId ? invites : [],
    loading: orgId === null
      ? false
      : loadingOrgIdRef.current === orgId
        ? loading
        : true,
    error: errorOrgIdRef.current === orgId ? error : null,
    errorKind: errorOrgIdRef.current === orgId ? errorKind : null,
    refresh,
    create,
    resend,
    revoke,
  };
}
